import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Login = () => {
  const [email, setEmail] = useState(process.env.REACT_APP_ADMIN_EMAIL || 'md.khajapasha.mohd@gmail.com');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const logoutReason = sessionStorage.getItem('logout_reason');
  if (logoutReason) sessionStorage.removeItem('logout_reason');
  const routerReason = location.state?.reason;
  const reasonMsg = routerReason
    || (logoutReason === 'idle_timeout' ? 'Logged out due to inactivity'
    : logoutReason === 'session_expired' ? 'Session expired — please log in again'
    : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(' '));
      } else {
        setError('Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" 
         style={{
           backgroundImage: 'url(https://images.unsplash.com/photo-1771873679764-4e5503b69040?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBncmlkJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzUzNDQwMjF8MA&ixlib=rb-4.1.0&q=85)',
           backgroundSize: 'cover',
           backgroundPosition: 'center'
         }}>
      <div className="absolute inset-0 bg-black/70"></div>
      <div className="relative w-full max-w-md">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-8">
          <h1 className="text-3xl font-['Outfit'] font-bold text-white mb-2" data-testid="login-title">Sign In</h1>
          <p className="text-[#A1A1AA] mb-6">Welcome to Ultimate ProMax Trading</p>

          {error && (
            <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 rounded-sm mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          {reasonMsg && !error && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-3 rounded-sm mb-4 text-sm" data-testid="session-expired-msg">
              {reasonMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-white mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-white mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white"
                required
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white font-medium"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm text-[#007AFF] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A1A1AA]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#007AFF] hover:underline" data-testid="register-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
